import gql from 'graphql-tag';

/**
 * GraphQL queries for Quick Notes Reporting functionality
 * 
 * This file centralizes all GraphQL queries used across the Quick Notes reporting components
 * to ensure consistency, maintainability, and reusability.
 * 
 * Query Categories:
 * 1. Aggregate Queries - For chart data and summaries
 * 2. Drill-down Queries - For detailed note lists
 * 3. Utility Queries - For dropdowns and metadata
 */

// =============================================================================
// AGGREGATE QUERIES - For Chart Data
// =============================================================================

/**
 * Year-to-Date aggregated data by month
 * Used by: QuickNotesYtdChart
 */
export const GET_QUICK_NOTES_YTD_AGGREGATES = gql`
  query GetQuickNotesYtdAggregates(
    $stack: QuickNotesStackEnum!
  ) {
    getQuickNotesYtdAggregates(
      stack: $stack
    ) {
      month
      buckets {
        key
        count
        timeSpentMinutes
      }
      totals {
        count
        timeSpentMinutes
      }
    }
  }
`;

/**
 * Monthly aggregated data by owner/user
 * Used by: QuickNotesOwnerChart
 */
export const GET_QUICK_NOTES_MONTHLY_BY_OWNER = gql`
  query GetQuickNotesMonthlyByOwner(
    $month: String!
    $stack: QuickNotesStackEnum!
  ) {
    getQuickNotesMonthlyByOwner(
      month: $month
      stack: $stack
    ) {
      ownerId
      ownerName
      buckets {
        key
        count
        timeSpentMinutes
      }
      totals {
        count
        timeSpentMinutes
      }
    }
  }
`;

/**
 * Monthly aggregated data by category
 * Used by: QuickNotesCategoryChart
 */
export const GET_QUICK_NOTES_MONTHLY_BY_CATEGORY = gql`
  query GetQuickNotesMonthlyByCategory(
    $month: String!
    $stack: QuickNotesStackEnum!
  ) {
    getQuickNotesMonthlyByCategory(
      month: $month
      stack: $stack
    ) {
      categoryId
      categoryName
      buckets {
        key
        count
        timeSpentMinutes
      }
      totals {
        count
        timeSpentMinutes
      }
    }
  }
`;

// =============================================================================
// DRILL-DOWN QUERIES - For Detailed Data
// =============================================================================

/**
 * Detailed Quick Notes for drill-down (paginated)
 * Used by: QuickNotesDrilldownModal
 */
export const GET_QUICK_NOTES_DRILLDOWN = gql`
  query GetQuickNotesDrilldown(
    $month: String
    $startDate: String
    $endDate: String
    $stack: QuickNotesStackEnum!
    $stackKey: String!
    $ownerId: ID
    $categoryId: ID
    $limit: Int
    $offset: Int
    $orderBy: String
    $order: String
  ) {
    getQuickNotesDrilldown(
      month: $month
      start_date: $startDate
      end_date: $endDate
      stack: $stack
      stack_key: $stackKey
      owner_id: $ownerId
      category_id: $categoryId
      limit: $limit
      offset: $offset
      order_by: $orderBy
      order: $order
    ) {
      id
      commentText
      timeSpentMinutes
      timeSpentFormatted
      interactionDate
      createdAt
      planned
      user {
        id
        user_name
      }
      commentable {
        id
        name
        user_name
      }
      activityType {
        id
        name
      }
      quickNoteCategory {
        id
        name
      }
    }
  }
`;

/**
 * Count of Quick Notes for drill-down pagination
 * Used by: QuickNotesDrilldownModal
 */
export const GET_QUICK_NOTES_DRILLDOWN_COUNT = gql`
  query GetQuickNotesDrilldownCount(
    $month: String
    $startDate: String
    $endDate: String
    $stack: QuickNotesStackEnum!
    $stackKey: String!
    $ownerId: ID
    $categoryId: ID
  ) {
    getQuickNotesDrilldownCount(
      month: $month
      start_date: $startDate
      end_date: $endDate
      stack: $stack
      stack_key: $stackKey
      owner_id: $ownerId
      category_id: $categoryId
    )
  }
`;

// =============================================================================
// UTILITY QUERIES - For Dropdowns and Metadata
// =============================================================================

/**
 * Get Quick Note categories for filtering and display
 * Used by: Various components for category dropdowns
 */
export const GET_QUICK_NOTE_CATEGORIES = gql`
  query GetQuickNoteCategories {
    getQuickNoteCategories {
      id
      name
      description
      active
    }
  }
`;

/**
 * Get current user information
 * Used by: Container components for context
 */
export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      user_name
      roles
      company {
        id
        name
      }
    }
  }
`;

/**
 * Search inventory users for autocomplete (if needed for filters)
 * Used by: Advanced filtering components
 */
export const SEARCH_INVENTORY_USERS_FOR_QUICK_NOTES = gql`
  query SearchInventoryUsersForQuickNotes(
    $searchTerm: String
    $limit: Int
  ) {
    searchInventoryUsersForQuickNotes(
      search_term: $searchTerm
      limit: $limit
    ) {
      id
      name
      allowedName
      city
      province
    }
  }
`;

// =============================================================================
// QUERY FRAGMENTS - For Reusable Pieces
// =============================================================================

/**
 * Common bucket data fragment
 * Used across multiple queries for consistency
 */
export const BUCKET_FRAGMENT = gql`
  fragment BucketData on QuickNotesBucketType {
    key
    count
    timeSpentMinutes
  }
`;

/**
 * Common totals data fragment
 * Used across multiple queries for consistency
 */
export const TOTALS_FRAGMENT = gql`
  fragment TotalsData on QuickNotesTotalsType {
    count
    timeSpentMinutes
  }
`;

/**
 * Common note data fragment
 * Used for detailed note queries
 */
export const NOTE_FRAGMENT = gql`
  fragment NoteData on NoteType {
    id
    commentText
    timeSpentMinutes
    timeSpentFormatted
    interactionDate
    createdAt
    planned
    user {
      id
      allowedName
    }
    commentable {
      id
      name
      allowedName
    }
    activityType {
      id
      name
    }
    quickNoteCategory {
      id
      name
    }
  }
`;

// =============================================================================
// QUERY HELPERS - Default Variables and Options
// =============================================================================

/**
 * Default variables for YTD queries
 */
export const DEFAULT_YTD_VARIABLES = {
  stack: 'ACTIVITY_TYPE',
  orderBy: 'month',
  order: 'asc'
};

/**
 * Default variables for monthly queries
 */
export const DEFAULT_MONTHLY_VARIABLES = {
  stack: 'ACTIVITY_TYPE',
  orderBy: 'total_count',
  order: 'desc'
};

/**
 * Default variables for drill-down queries
 */
export const DEFAULT_DRILLDOWN_VARIABLES = {
  limit: 50,
  offset: 0,
  orderBy: 'interaction_date',
  order: 'desc'
};

/**
 * Default Apollo query options for error handling
 */
export const DEFAULT_QUERY_OPTIONS = {
  errorPolicy: 'all',
  notifyOnNetworkStatusChange: true,
  fetchPolicy: 'cache-and-network'
};

/**
 * Query options for real-time data (skip caching)
 */
export const REALTIME_QUERY_OPTIONS = {
  errorPolicy: 'all',
  fetchPolicy: 'network-only',
  notifyOnNetworkStatusChange: true
};

// =============================================================================
// QUERY BUILDERS - Helper Functions
// =============================================================================

/**
 * Build variables for YTD aggregates query
 * @param {string} stack - Stack dimension ('ACTIVITY_TYPE', 'PLANNED', 'CATEGORY')
 * @param {string} orderBy - Order by field
 * @param {string} order - Sort order ('asc', 'desc')
 * @returns {Object} Variables object for GraphQL query
 */
export const buildYtdVariables = (stack = 'ACTIVITY_TYPE', orderBy = 'month', order = 'asc') => ({
  stack: stack.toUpperCase(),
  orderBy,
  order: order.toLowerCase()
});

/**
 * Build variables for monthly by owner query
 * @param {string} month - Month in YYYY-MM format
 * @param {string} stack - Stack dimension
 * @param {string} orderBy - Order by field
 * @param {string} order - Sort order
 * @returns {Object} Variables object for GraphQL query
 */
export const buildOwnerVariables = (month, stack = 'ACTIVITY_TYPE', orderBy = 'owner_name', order = 'asc') => ({
  month,
  stack: stack.toUpperCase(),
  orderBy,
  order: order.toLowerCase()
});

/**
 * Build variables for monthly by category query
 * @param {string} month - Month in YYYY-MM format
 * @param {string} stack - Stack dimension
 * @param {string} orderBy - Order by field
 * @param {string} order - Sort order
 * @returns {Object} Variables object for GraphQL query
 */
export const buildCategoryVariables = (month, stack = 'ACTIVITY_TYPE', orderBy = 'category_name', order = 'asc') => ({
  month,
  stack: stack.toUpperCase(),
  orderBy,
  order: order.toLowerCase()
});

/**
 * Build variables for drill-down query
 * @param {Object} options - Drill-down options
 * @param {string} options.stack - Stack dimension
 * @param {string} options.stackKey - Stack key value
 * @param {string} options.month - Month filter (optional)
 * @param {string} options.startDate - Start date filter (optional)
 * @param {string} options.endDate - End date filter (optional)
 * @param {string} options.ownerId - Owner filter (optional)
 * @param {string} options.categoryId - Category filter (optional)
 * @param {number} options.limit - Page size
 * @param {number} options.offset - Page offset
 * @param {string} options.orderBy - Order by field
 * @param {string} options.order - Sort order
 * @returns {Object} Variables object for GraphQL query
 */
export const buildDrilldownVariables = ({
  stack,
  stackKey,
  month = null,
  startDate = null,
  endDate = null,
  ownerId = null,
  categoryId = null,
  limit = 50,
  offset = 0,
  orderBy = 'interaction_date',
  order = 'desc'
}) => ({
  stack: stack.toUpperCase(),
  stackKey,
  month,
  startDate,
  endDate,
  ownerId,
  categoryId,
  limit,
  offset,
  orderBy,
  order: order.toLowerCase()
});

/**
 * Build variables for drill-down count query
 * @param {Object} options - Same as buildDrilldownVariables but without pagination
 * @returns {Object} Variables object for GraphQL count query
 */
export const buildDrilldownCountVariables = ({
  stack,
  stackKey,
  month = null,
  startDate = null,
  endDate = null,
  ownerId = null,
  categoryId = null
}) => ({
  stack: stack.toUpperCase(),
  stackKey,
  month,
  startDate,
  endDate,
  ownerId,
  categoryId
});

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/**
 * Validate stack dimension value
 * @param {string} stack - Stack dimension to validate
 * @returns {boolean} Whether the stack dimension is valid
 */
export const isValidStackDimension = (stack) => {
  const validStacks = ['ACTIVITY_TYPE', 'PLANNED', 'CATEGORY'];
  return validStacks.includes(stack.toUpperCase());
};

/**
 * Validate month format
 * @param {string} month - Month string to validate (YYYY-MM)
 * @returns {boolean} Whether the month format is valid
 */
export const isValidMonthFormat = (month) => {
  if (!month) return false;
  const monthRegex = /^\d{4}-\d{2}$/;
  return monthRegex.test(month);
};

/**
 * Validate date format
 * @param {string} date - Date string to validate (YYYY-MM-DD)
 * @returns {boolean} Whether the date format is valid
 */
export const isValidDateFormat = (date) => {
  if (!date) return false;
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  return dateRegex.test(date);
};

/**
 * Validate order direction
 * @param {string} order - Order direction to validate
 * @returns {boolean} Whether the order direction is valid
 */
export const isValidOrderDirection = (order) => {
  const validOrders = ['asc', 'desc'];
  return validOrders.includes(order.toLowerCase());
};
